import { useState, useEffect, useMemo } from 'react'
import { Typography, Box, Stack, Tabs, Tab, LinearProgress, Chip, Select, MenuItem, FormControl, Button } from '@mui/material'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { useIsMobile } from '@/hooks/Responsive'
import LectureListTab from './LectureListTab'
import IntroductionTab from './IntroductionTab'
import NoticeTab from './NoticeTab'
// import SurveyTab from './SurveyTab'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { fetchMyCourseDetailComboList } from '@/api/mypage'
import { useAuth } from '@/components/AuthContext'

export function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

export default function MyCoursesView() {
  const { user } = useAuth();
  const navigate = useNavigate()
  const lang = useLang() 
  const [searchParams, setSearchParams] = useSearchParams()
  const { id } = useParams()
  const { isMobile } = useIsMobile()

  const [data, setData] = useState(null)
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchCourseData = async () => {
      if (!id) return

      try {
        setLoading(true)
        // NOTE: 실제 API 엔드포인트는 환경에 맞게 조정해야 합니다.
        // 우선 하나의 API 호출로 모든 강좌 목록을 가져온 후,
        // 그 중에서 id에 해당하는 강좌를 찾는 로직으로 구현합니다.
        const response = await fetchMyCourseDetailComboList({params: {accountId: user.accountId}});

        const currentCourse = response?.data.find(course => String(course.eduLectureId) === String(id))//id
        // const currentCourse = 'OPVL-2025-00001';
        
        setAllItems(response.data)
        setData(currentCourse || null)

      } catch (error) {
        console.error("Failed to fetch course data:", error)
        setData(null)
        setAllItems([])
      } finally {
        setLoading(false)
      }
    }

    fetchCourseData()
  }, [id])

  // 탭 관련
  const tabKeys = ['list', 'intro', 'notice', 'survey']
  const initialTab =
    tabKeys.indexOf(searchParams.get('tab')) >= 0
      ? tabKeys.indexOf(searchParams.get('tab'))
      : 0
  const [value, setValue] = useState(initialTab)

  const handleChange = (event, newValue) => {
    const newTab = tabKeys[newValue]
    const current = Object.fromEntries(searchParams.entries())
    const nextParams = { ...current, tab: newTab }
    delete nextParams.id
    const queryString = new URLSearchParams(nextParams).toString()
    const targetUrl = `${window.location.pathname}?${queryString}`

    window.history.pushState({}, '', targetUrl)
    setSearchParams(nextParams)
    setValue(newValue)
  }

  const handleLectureChange = (e) => {
    const nextId = e.target.value
    navigate(`/mypage/courses/${nextId}?tab=${searchParams.get('tab') ?? 'list'}`)
  }

  useEffect(() => {
    const urlTab = searchParams.get('tab')
    const idx = tabKeys.indexOf(urlTab)
    if (idx >= 0 && idx !== value) setValue(idx)
  }, [searchParams])

  if (loading) {
    return <Typography sx={{ py: 5, textAlign: 'center' }}>Loading...</Typography>
  }

  if (!data) {
    return <Typography sx={{ py: 5, textAlign: 'center' }}>강좌 정보를 찾을 수 없습니다.</Typography>
  }

  const statusColor = {
    신청대기:	'secondary2',
    신청취소:	'grey',
    신청반려:	'error',
    신청승인:	'secondary',
    취소대기:	'grey',
    수강취소:	'grey',
    수강중: 'primary',
    수강완료:	'secondary2',
  }[data.signupProgressStateName] || 'grey'

  // 진도율 
  const progress = Math.round((data.session / data.totalSession) * 100)

  return (
    <Box>
      {/* 상단 강좌 정보 */}
      <Box
        sx={{
          width: '100%',
          bgcolor: 'grey.50',
          borderRadius: 2,
          py: 2,
          px: isMobile ? 2 : 3,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: isMobile? 5 : 6,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {data?.signupProgressStateName && (
            <Chip label={data.signupProgressStateName} variant="outlined" color={statusColor} />
           )}
          <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '320px' }}>
            <Select
              className="transparent"
              name="lecture"
              value={data.eduLectureId}
              onChange={handleLectureChange}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: (theme) => 
                      isMobile ? `calc(100vw - ${theme.spacing(20)})` : '100%',
                    maxWidth: (theme) =>
                      isMobile ? `calc(100vw - ${theme.spacing(20)})` : '320px',
                  },
                },
              }}
              sx={{
                width: (theme) => 
                  isMobile ? `calc(100vw - ${theme.spacing(20)})` : '100%',
                maxWidth: (theme) =>
                  isMobile ? `calc(100vw - ${theme.spacing(20)})` : '320px',
                typography: 'subtitle2',
                fontWeight: '600'
              }}
            >
              {allItems.map((item) => (
                <MenuItem key={item.eduLectureId} value={item.eduLectureId}>
                  {item.eduLectureName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          sx={{
            flexShrink: 0,
            minWidth: isMobile ? 'auto' : '260px',
            width: isMobile ? '100%' : 'auto',
            columnGap: 2,
            rowGap: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>
              진도율
            </Typography>
            <Box sx={{ width: isMobile ? '100px' : '120px' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: '8px',
                  borderRadius: 1,
                  bgcolor: 'divider',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'primary.main',
                  },
                }}
              />
            </Box>
            <Typography>
              {progress}%
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center">
            <Typography>회차</Typography>
            <Box
              sx={{
                width: '1px',
                height: '12px',
                bgcolor: 'divider',
                mx: 1,
              }}
            />
            <Box component="span" sx={{ color: 'primary.main' }}>{data.session}</Box>/{data.totalSession}
          </Stack>
        </Stack>
      </Box>

      {/* 탭 */}
      <Tabs
        value={value}
        onChange={handleChange}
        onClick={(e) => {
          const tabEls = e.currentTarget.querySelectorAll('.MuiTab-root')
          const clickedTab = e.target.closest('.MuiTab-root')

          if (!clickedTab) return 

          const clickedIndex = Array.from(tabEls).indexOf(clickedTab)

          if (clickedIndex === value) {
            handleChange(e, value)
          }
        }}
        variant="fullWidth"
        aria-label="나의 수강강좌 상세 탭"
        textColor="inherit"
        TabIndicatorProps={{
          sx: { height: '2px', bgcolor: 'grey.800' },
        }}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 2,
          '& .MuiTab-root': {
            color: 'grey.500',
            px: isMobile ? 2 : 4,
            flex: '1 1 0',
            typography: 'body1',
            fontWeight: '600',
            minWidth: 'initial',
            height: '48px',
            lineHeight: 1,
            textTransform: 'none',
          },
          '& .Mui-selected': {
            color: 'grey.800',
          },
        }}
      >
        <Tab label="강의목록" />
        <Tab label="소개" />
        <Tab label="공지사항" />
        {/* <Tab label="설문" /> */}
      </Tabs>

      {/* 탭 콘텐츠 */}
      <TabPanel value={value} index={0}>
        <LectureListTab />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <IntroductionTab />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <NoticeTab />
      </TabPanel>
      {/* <TabPanel value={value} index={3}>
        <SurveyTab />
      </TabPanel> */}

      {/* 하단 버튼 */}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: isMobile ? 1 : 3, gap: 1 }}
      >
        <Button
          variant="outlined"
          color="primary"
          size="large" 
          sx={{ width: isMobile ? '100%' : 'auto' }}
          onClick={() => navigate(buildLangHref('/mypage/courses', lang))}
        >
          강좌목록
        </Button>
      </Stack>
    </Box>
  )
}
